<template>
  <div class="app-layout">
    <div class="app-header">
      <img src="@/assets/imgs/logo.png" alt="logo" class="logo" />

      <div class="toolbar">
        <el-icon size="18" @click="showUserInfo" title="个人信息"><User /></el-icon>
        <el-icon size="18" @click="logout" title="退出登录"><SwitchButton /></el-icon>
      </div>
    </div>

    <div class="app-main">
      <div :class="foldSidebar ? 'fold' : ''" class="app-aside">
        <div class="fold-btn">
          <el-icon :title="foldSidebar ? '展开' : '折叠'" @click="foldSidebar = !foldSidebar">
            <component :is="foldSidebar ? 'Expand' : 'Fold'" />
          </el-icon>
        </div>

        <app-navigator :fold="foldSidebar" />
      </div>

      <div class="app-content">
        <div class="content-main">
          <div class="content-main__header">
            <el-breadcrumb separator="/">
              <template v-for="pathitem in breadcrumb" :key="pathitem.fullpath">
                <el-breadcrumb-item v-if="pathitem.disable">
                  <el-tag type="info">{{ pathitem.title }}</el-tag>
                </el-breadcrumb-item>

                <el-breadcrumb-item v-else :to="{ path: pathitem.fullpath }">
                  <el-tag type="primary">{{ pathitem.title }}</el-tag>
                </el-breadcrumb-item>
              </template>
            </el-breadcrumb>
          </div>

          <div class="content-main-body">
            <div class="content-body-box">
              <router-view />
            </div>
          </div>
        </div>

        <div class="content-footer">
          <el-link type="info" href="https://beian.miit.gov.cn/" :underline="false">蜀ICP备2025168507号-1</el-link>
          <el-text type="info">Copyright ©2025 成都唯思备安全科技有限公司. 版权所有.</el-text>
        </div>
      </div>
    </div>

    <el-dialog v-model="showUserInfoDialog" title="个人信息" width="fit-content" align-center>
      <el-descriptions :column="2" border>
        <el-descriptions-item label="姓名" width="180">
          {{ userinfo.name }}
        </el-descriptions-item>

        <el-descriptions-item label="账号" width="180">
          {{ userinfo.account }}
        </el-descriptions-item>

        <el-descriptions-item label="联系电话" width="180">
          {{ userinfo.telephone }}
        </el-descriptions-item>

        <el-descriptions-item label="角色" width="180">
          <template v-for="(role, index) in roles" :key="role">
            <span v-if="index !== 0" class="role-split">,</span>
            <el-tag type="primary">{{ roleMap[role]?.name }}</el-tag>
          </template>
        </el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { newRequest, newCall, clearLoginInfo, getLoginInfo } from '@/util/http'
import { ElMessage } from 'element-plus'
import { useAuthStore } from '@/store/auth'
import { roleMap } from '@/views/adminUser/model'
import AppNavigator from './AppNavigator.vue'

const authStore = useAuthStore()

const router = useRouter()
const route = useRoute()

const foldSidebar = ref(false)

const breadcrumb = computed(() => {
  const arr = []
  for (const item of route.matched) {
    if (item.meta.title) {
      arr.push({
        fullpath: item.path,
        title: item.meta.title,
        disable: item.meta?.layout === true || item.path === route.path
      })
    }
  }
  return arr
})

const showUserInfoDialog = ref(false)
const userInfoLoaded = ref(false)
const userinfo = ref({
  id: null,
  name: '',
  account: '',
  telephone: ''
})

const roles = ref([])

function showUserInfo() {
  showUserInfoDialog.value = true
  if (userInfoLoaded.value) return
  newRequest({
    url: 'get/info',
    loading: true,
    success: (data) => {
      userinfo.value = data
      userInfoLoaded.value = true
      roles.value = getLoginInfo().roles
    }
  })
}

async function logout() {
  await newCall({ url: 'logout', method: 'POST' })
  clearLoginInfo()
  authStore.clear()
  ElMessage.success('退出成功')
  router.push('/login')
}
</script>

<style lang="scss" scoped>
.app-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
}
.app-header {
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  border-bottom: var(--my-border);
  background-color: var(--my-bg-color);
  color: var(--my-text-color);
  padding: 0 20px;
  box-sizing: border-box;
}
.app-main {
  flex: 1;
  display: flex;
}
.app-aside {
  background-color: var(--my-bg-color);
  transition: width 0.5s;
  height: 100%;
  width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &.fold {
    width: 30px;
    transition: width 0.5s;
  }
}
.app-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.content-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  box-sizing: border-box;
}
.content-main__header {
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  border-bottom: var(--my-border);
  background-color: var(--my-bg-color);
  color: var(--my-text-color);
  box-sizing: border-box;
  padding: 0 20px;
}
.content-main-body {
  flex: 1;
  position: relative;
}
.content-body-box {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.content-footer {
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: var(--my-border);
  gap: 50px;
  box-sizing: border-box;
}
.toolbar {
  display: flex;
  align-items: center;
  width: fit-content;
  gap: 15px;

  & > * {
    color: var(--my-text-color);
    cursor: pointer;
  }
}
.fold-btn {
  color: var(--my-text-color);
  line-height: 2;
  cursor: pointer;
}
.logo {
  object-fit: contain;
  max-width: 300px;
  height: 50px;
}
.role-split {
  margin-right: 3px;
}
</style>
